import {
  ONBOARDING_EMAIL_DEFAULTS,
  ONBOARDING_EMAIL_KEYS,
  type OnboardingEmailKey,
  renderInviteEmail,
  renderOnboardingEmail,
} from '@uit/shared/email-template';
import {
  type OnboardingEmailsResponse,
  OnboardingEmailsResponse as OnboardingEmailsResponseSchema,
} from '@uit/shared/schemas/settings';
import { useEffect, useRef, useState } from 'react';
import { apiMutation, useApi } from '../../lib/api.js';
import s from '../../pages/SettingsPage.module.scss';
import c from './OnboardingEmailsSection.module.scss';

// Sample values for the genuinely per-onboarding fields. The signatory is the
// REAL configured value (from the API), so the preview matches what is sent.
const PREVIEW_SAMPLE: Record<string, string> = {
  contractorName: 'Christopher Wu',
  startDate: '07/01/2026',
  resourceName: 'Christopher Wu',
  clientName: 'Cigna',
};
// Sample one-time link for the invite preview — the real link is structural,
// appended by renderInviteEmail at send time.
const PREVIEW_INVITE_URL = 'https://portal.ultimumit.com/onboard/«one-time-token»';

type Draft = { subject: string; body: string };
type Drafts = Record<OnboardingEmailKey, Draft>;

export function OnboardingEmailsSection() {
  const { data } = useApi<OnboardingEmailsResponse>(
    '/api/settings/onboarding-emails',
    OnboardingEmailsResponseSchema,
  );
  const [active, setActive] = useState<OnboardingEmailKey>('post_sig_1');
  const [drafts, setDrafts] = useState<Drafts | null>(null);
  const [signatory, setSignatory] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (data) {
      setDrafts(
        Object.fromEntries(ONBOARDING_EMAIL_KEYS.map((k) => [k, { ...data[k] }])) as Drafts,
      );
      setSignatory(data.signatoryName);
    }
  }, [data]);

  if (!drafts) {
    return (
      <section className={s.section}>
        <h2 className={s.sectionTitle}>Onboarding emails</h2>
        <p className={s.sectionSubtitle}>Loading…</p>
      </section>
    );
  }

  const draft = drafts[active];
  const meta = ONBOARDING_EMAIL_DEFAULTS[active];
  const preview =
    active === 'invite'
      ? renderInviteEmail(
          draft,
          {
            resourceName: PREVIEW_SAMPLE['resourceName']!,
            clientName: PREVIEW_SAMPLE['clientName']!,
          },
          PREVIEW_INVITE_URL,
        )
      : renderOnboardingEmail(draft, { ...PREVIEW_SAMPLE, signatoryName: signatory });

  const update = (patch: Partial<Draft>): void => {
    setDrafts((d) => (d ? { ...d, [active]: { ...d[active], ...patch } } : d));
    setSuccess(null);
  };

  const insertPlaceholder = (p: string): void => {
    const ta = bodyRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const tok = `{{${p}}}`;
    update({ body: draft.body.slice(0, start) + tok + draft.body.slice(end) });
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + tok.length;
    });
  };

  const applyResponse = (resp: OnboardingEmailsResponse): void => {
    setDrafts(
      Object.fromEntries(ONBOARDING_EMAIL_KEYS.map((k) => [k, { ...resp[k] }])) as Drafts,
    );
    setSignatory(resp.signatoryName);
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await apiMutation<
        { emailKey: OnboardingEmailKey; subject: string; body: string },
        OnboardingEmailsResponse
      >(
        '/api/settings/onboarding-emails',
        { emailKey: active, subject: draft.subject, body: draft.body },
        { method: 'PATCH', schema: OnboardingEmailsResponseSchema },
      );
      applyResponse(resp);
      setSuccess('Saved.');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message.includes('403') ? 'You do not have permission.' : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await apiMutation<Record<string, never>, OnboardingEmailsResponse>(
        `/api/settings/onboarding-emails/${active}`,
        {},
        { method: 'DELETE', schema: OnboardingEmailsResponseSchema },
      );
      applyResponse(resp);
      setSuccess('Reset to default.');
    } catch {
      setError('Could not reset.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Onboarding emails</h2>
      <p className={s.sectionSubtitle}>
        The contractor-facing onboarding emails: the magic-link invite, then the two sent once the
        contract is fully signed. Written in markdown — the preview is exactly what gets sent.
      </p>

      <div className={c.tabs} role="tablist">
        {ONBOARDING_EMAIL_KEYS.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={k === active}
            className={`${c.tab} ${k === active ? c.tabActive : ''}`}
            onClick={() => {
              setActive(k);
              setSuccess(null);
              setError(null);
            }}
          >
            {ONBOARDING_EMAIL_DEFAULTS[k].label}
          </button>
        ))}
      </div>

      <p className={c.meta}>{meta.description}</p>

      <div className={s.field}>
        <label className={s.label} htmlFor="oe-subject">
          Subject
        </label>
        <input
          id="oe-subject"
          className={s.input}
          value={draft.subject}
          onChange={(e) => update({ subject: e.target.value })}
        />
      </div>

      <div className={s.field}>
        <label className={s.label} htmlFor="oe-body">
          Body (markdown)
        </label>
        <div className={c.placeholders}>
          <span className={c.phLabel}>Insert:</span>
          {meta.placeholders.map((p) => (
            <button key={p} type="button" className={c.ph} onClick={() => insertPlaceholder(p)}>
              {`{{${p}}}`}
            </button>
          ))}
        </div>
        <textarea
          id="oe-body"
          ref={bodyRef}
          className={c.textarea}
          value={draft.body}
          spellCheck
          onChange={(e) => update({ body: e.target.value })}
        />
      </div>

      <div className={s.buttonRow}>
        <button type="button" className={s.button} onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          className={c.linkBtn}
          onClick={() => void resetToDefault()}
          disabled={saving}
        >
          Reset to default
        </button>
      </div>

      {success && (
        <div className={s.success} role="status">
          {success}
        </div>
      )}
      {error && (
        <div className={s.error} role="alert">
          {error}
        </div>
      )}

      <div className={c.previewLabel}>
        Preview <span className={c.live}>· what the contractor sees</span>
      </div>
      <div className={c.emailCard}>
        <div className={c.emailHead}>
          <div className={c.headRow}>
            <span className={c.headKey}>From</span>
            <span>ultimumIT &lt;no-reply@send.ultimumit.com&gt;</span>
          </div>
          <div className={c.headRow}>
            <span className={c.headKey}>Subject</span>
            <span className={c.headSubj}>{preview.subject}</span>
          </div>
        </div>
        <div className={c.emailContent}>
          {/* preview.html is markdown-it html:false output with escaped values — safe. */}
          <div dangerouslySetInnerHTML={{ __html: preview.html }} />
          {active === 'post_sig_1' && (
            <div className={c.attach}>
              📎 The active policy documents are attached automatically — managed in the Policy
              Documents section.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
